import React, { useState, useEffect } from 'react';
import { Box, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { getMarkets, getCountries } from '../../../../features/master-data';
import {
  addMarket,
  updateMarket,
  deleteMarket,
} from '../../services/SettingsRepository';
import ExpSnackbarAlerts from '../../../../shared/components/display/ExpSnackbarAlerts';
import MultiSelectColumnFilter from '../../../../shared/components/datagrid/MultiSelectColumnFilter';
import useSnackbar from '../../../../shared/hooks/useSnackbar';
import useRowEditing from '../../../../shared/hooks/useRowEditing';
import getActionsColumn from '../../../../shared/components/datagrid/getActionsColumn';
import AddRowToolbar from '../../../../shared/components/datagrid/AddRowToolbar';
import { scrollToNewRow } from '../../../../shared/utils/scrollToNewRow';

const MarketTable = () => {
  const [markets, setMarkets] = useState([]);
  const [countries, setCountries] = useState([]);
  const [nextId, setNextId] = useState(1);
  const { showSnackbar, snackbarProps } = useSnackbar();
  const apiRef = useGridApiRef();

  const [filterModel, setFilterModel] = useState(() => {
    const saved = localStorage.getItem('marketTableFilter');
    return saved ? JSON.parse(saved) : { items: [] };
  });

  useEffect(() => {
    localStorage.setItem('marketTableFilter', JSON.stringify(filterModel));
  }, [filterModel]);

  const {
    rowModesModel,
    handleRowEditStop,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
    startEditingRow,
  } = useRowEditing({ rows: markets, setRows: setMarkets });

  const fetchMarkets = async () => {
    try {
      const marketsResponse = await getMarkets();
      const marketsWithCountry = marketsResponse.map((market) => ({
        ...market,
        country:
          market.country?.countryCode && market.country?.name
            ? {
                countryCode: market.country.countryCode,
                name: market.country.name,
                salesDay: market.country.salesDay || null,
              }
            : null,
      }));
      setMarkets(marketsWithCountry);
      setNextId(
        marketsWithCountry.length
          ? Math.max(...marketsWithCountry.map((m) => m.id)) + 1
          : 1
      );
    } catch (error) {
      console.error('Error fetching markets:', error);
      showSnackbar('Error fetching markets.', 'error');
    }
  };

  const fetchCountries = async () => {
    try {
      setCountries(await getCountries());
    } catch (error) {
      console.error('Error fetching countries:', error);
      showSnackbar('Error fetching countries.', 'error');
    }
  };

  useEffect(() => {
    fetchMarkets();
    fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteClick = (id) => async () => {
    try {
      await deleteMarket(id);
      await fetchMarkets();
      showSnackbar('Market deleted successfully!');
    } catch (error) {
      console.error('Error deleting market:', error);
      showSnackbar('Error deleting market.', 'error');
    }
  };

  const processRowUpdate = async (newRow) => {
    const idNum = Number(newRow.id);
    if (
      newRow.id === '' ||
      isNaN(idNum) ||
      idNum < 0 ||
      idNum > 999 ||
      !Number.isInteger(idNum)
    ) {
      showSnackbar('ID must be a whole number between 0 and 999.', 'error');
      throw new Error('ID must be a whole number between 0 and 999.');
    }

    if (newRow.isNew) {
      const existingMarket = markets.find((m) => m.id === idNum && !m.isNew);
      if (existingMarket) {
        showSnackbar(`A market with ID ${idNum} already exists.`, 'error');
        throw new Error(`A market with ID ${idNum} already exists.`);
      }
    }

    try {
      if (newRow.isNew) {
        await addMarket(newRow);
        showSnackbar('Market added successfully!');
      } else {
        await updateMarket(newRow.id, newRow);
        showSnackbar('Market updated successfully!');
      }
      await fetchMarkets();
    } catch (error) {
      console.error('Error saving market:', error);
      showSnackbar('Error saving market.', 'error');
      if (newRow.isNew) {
        setMarkets((prev) => prev.filter((row) => row.id !== newRow.id));
      }
      throw error;
    }
    return { ...newRow, isNew: false };
  };

  const handleAddClick = () => {
    const newId = nextId;
    setNextId(newId + 1);
    setMarkets((oldRows) => [
      ...oldRows,
      { id: newId, name: '', country: null, salesDay: null, isNew: true },
    ]);
    startEditingRow(newId, 'id');
    // Clear any active filters so the new row is visible, then scroll to it
    setFilterModel({ items: [] });
    scrollToNewRow(apiRef, markets.length + 1);
  };

  const marketColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 150,
      editable: true,
      renderEditCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            px: 1,
          }}
        >
          <TextField
            value={params.value != null ? params.value : ''}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || (/^\d+$/.test(val) && Number(val) <= 999)) {
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'id',
                  value: val === '' ? '' : Number(val),
                });
              }
            }}
            inputProps={{
              maxLength: 3,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { padding: 0 },
            }}
            placeholder='0'
            size='small'
            variant='standard'
            sx={{ flex: 1 }}
          />
        </Box>
      ),
    },
    { field: 'name', headerName: 'Market Name', width: 300, editable: true },
    {
      field: 'country',
      headerName: 'Country',
      width: 250,
      editable: true,
      filterable: true,
      renderCell: (params) =>
        params?.row?.country?.countryCode && params.row.country.name
          ? `${params.row.country.countryCode} - ${params.row.country.name}`
          : 'N/A',
      filterOperators: [
        {
          label: 'is any of',
          value: 'isAnyOf',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value || filterItem.value.length === 0) return null;
            return (params) => {
              const countryCode = params?.countryCode;
              if (!countryCode) return false;
              return filterItem.value.includes(countryCode);
            };
          },
          InputComponent: (props) => {
            const countryOptions = [
              ...new Map(
                markets
                  .filter((m) => m.country?.countryCode && m.country?.name)
                  .map((m) => [
                    m.country.countryCode,
                    {
                      value: m.country.countryCode,
                      label: `${m.country.countryCode} - ${m.country.name}`,
                    },
                  ])
              ).values(),
            ].sort((a, b) => a.label.localeCompare(b.label));
            return (
              <MultiSelectColumnFilter
                item={props.item}
                applyValue={props.applyValue}
                options={countryOptions}
              />
            );
          },
        },
      ],
      sortComparator: (v1, v2) => {
        const codeCompare = (v1?.countryCode || '').localeCompare(
          v2?.countryCode || ''
        );
        return codeCompare !== 0
          ? codeCompare
          : (v1?.name || '').localeCompare(v2?.name || '');
      },
      renderEditCell: (params) => (
        <Autocomplete
          options={countries}
          getOptionLabel={(option) =>
            option?.countryCode && option?.name
              ? `${option.countryCode} - ${option.name}`
              : 'N/A'
          }
          value={params?.row?.country || null}
          isOptionEqualToValue={(option, value) =>
            value
              ? option.countryCode === value.countryCode &&
                option.name === value.name
              : false
          }
          onChange={(_, newValue) => {
            params.api.setEditCellValue({
              id: params.id,
              field: 'country',
              value: newValue || null,
            });
          }}
          renderInput={(inputProps) => (
            <TextField {...inputProps} label='Country' sx={{ width: 230 }} />
          )}
        />
      ),
    },
    {
      field: 'salesDay',
      headerName: 'Sales Day',
      type: 'number',
      width: 150,
      editable: true,
    },
    getActionsColumn({
      rowModesModel,
      handleEditClick,
      handleSaveClick,
      handleCancelClick,
      handleDeleteClick,
    }),
  ];

  return (
    <Box height='50vh'>
      <DataGridPro
        apiRef={apiRef}
        pagination
        rows={markets}
        columns={marketColumns}
        getRowId={(row) => row.id}
        editMode='row'
        // ID is editable only for new rows; isCellEditable prevents editing on existing rows
        isCellEditable={(params) => params.field !== 'id' || !!params.row.isNew}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) =>
          console.error('Row update error:', error)
        }
        filterMode='client'
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        slots={{ toolbar: AddRowToolbar }}
        slotProps={{ toolbar: { label: 'Add Market', onAdd: handleAddClick } }}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          columns: { columnVisibilityModel: { id: true } },
        }}
      />
      <ExpSnackbarAlerts {...snackbarProps} />
    </Box>
  );
};

export default MarketTable;
